import { useCallback, useMemo } from "react";
import { useAppSelector } from "@/app/store";
import { type PermissionCode, STATUS } from "@/constants";
import {
  selectAccessiblePermissions,
  selectCurrentUserStatus,
  selectDomainRestrictedPermissions,
} from "@/features/users/model/usersSlice";

export type UsePermissionsResult = {
  /** True when the logged-in user holds `code` via any domain-role grant (or is an Administrator). */
  has: (code: PermissionCode) => boolean;
  /** True when the user holds at least one of `codes`. */
  hasAny: (...codes: PermissionCode[]) => boolean;
  /**
   * True when the user holds `code` **for `domainId` specifically**.
   *
   * Use this for any control on a route the API gates per domain — the domain Access tab and its
   * grant/revoke buttons today. `has()` answers "anywhere", which is right for a workspace-wide
   * capability and wrong here: a role granting `domain-access-add` on one domain would otherwise
   * light up the button on every domain the user can open, and the API would 403 on click.
   *
   * A permission the user holds workspace-wide is unrestricted and passes for every domain,
   * including ones created after the grant. Passing `null` (no domain resolved yet) is false,
   * keeping the gate fail-closed.
   */
  hasForDomain: (code: PermissionCode, domainId: number | null) => boolean;
  /**
   * True once `/users/logged-in` has settled. Permissions read as empty until then, so gating is
   * fail-closed: controls appear after resolution rather than flashing then disappearing.
   */
  isResolved: boolean;
};

/**
 * Permission checks for the logged-in user, typed against the backend permission catalog.
 *
 * Prefer this over reading `selectAccessiblePermissions` and calling `.includes()` directly — the
 * `PermissionCode` parameter turns a mistyped code into a compile error.
 */
export function usePermissions(): UsePermissionsResult {
  const permissions = useAppSelector(selectAccessiblePermissions);
  const domainRestrictions = useAppSelector(selectDomainRestrictedPermissions);
  const status = useAppSelector(selectCurrentUserStatus);

  const has = useCallback(
    (code: PermissionCode) => permissions.includes(code),
    [permissions],
  );

  const hasAny = useCallback(
    (...codes: PermissionCode[]) => codes.some((code) => permissions.includes(code)),
    [permissions],
  );

  const hasForDomain = useCallback(
    (code: PermissionCode, domainId: number | null) => {
      if (!permissions.includes(code)) {
        return false;
      }
      const allowedDomainIds = domainRestrictions[code];
      // Absent means unrestricted -- the backend omits a code held workspace-wide, mirroring
      // `resolve_domain_ids` returning None.
      if (allowedDomainIds === undefined) {
        return true;
      }
      return domainId !== null && allowedDomainIds.includes(domainId);
    },
    [permissions, domainRestrictions],
  );

  const isResolved = status === STATUS.SUCCEEDED || status === STATUS.FAILED;

  return useMemo(
    () => ({ has, hasAny, hasForDomain, isResolved }),
    [has, hasAny, hasForDomain, isResolved],
  );
}
